import React, { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import Step1ProductInfo from "../components/steps/Step1ProductInfo";
import Step2MaterialAcquisition from "../components/steps/Step2MaterialAcquisition";
import Step3Packaging from "../components/steps/Step3Packaging";
import Step4AssemblyDistribution from "../components/steps/Step4AssemblyDistribution";
import AiPrefillReview from "../components/lca/AiPrefillReview";
import AskManufacturerBar, { flagIdentity } from "../components/manufacturer/AskManufacturerBar";
import RequestModal from "../components/manufacturer/RequestModal";
import TrialDailyLimitModal from "../components/TrialDailyLimitModal";
import { MANUFACTURER_REQUESTS_LAUNCHED } from "../config/featureFlags";
import { clearSupplierCache } from "../utils/productListCache";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

// Great-circle distance (km) between two {lat, lon} points. Mirrors the
// estimate Step 4 shows so a prefilled destination gets a sensible distance.
const toRad = (deg) => (deg * Math.PI) / 180;
const haversineKm = (a, b) => {
  if (!a || !b) return 0;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLon = Math.sin(dLon / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLon * sinDLon;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h)));
};
// A packaging value meaning "no packaging" — mirrors the modal's
// LOOSE_PACKED_VALUE_RE and the server's LOOSE_PACKED_RE.
const LOOSE_PACKED_VALUE_RE = /^loose\s*pack(ed)?\.?\s*$/i;

// Approximate real-world route by mode (sea routes ~1.2x the straight line).
const getModeMultiplier = (mode) => {
  const m = (mode || "").toLowerCase();
  if (m.includes("air")) return 1.0;
  if (m.includes("rail") || m.includes("train")) return 1.15;
  if (m.includes("road") || m.includes("truck") || m.includes("hgv") || m.includes("van")) return 1.3;
  return 1.2; // sea / default
};

// Major export seaport for a country of origin. When a product only tells us the
// country (e.g. "China") and has no specific origin address, we assume goods ship
// from that country's primary container port — a sensible default that makes the
// transport leg (and the AI assessment) usable without manual entry.
const COUNTRY_MAJOR_PORTS = {
  china: { address: "Port of Shanghai, China", lat: 31.23, lng: 121.47 },
  "hong kong": { address: "Port of Hong Kong", lat: 22.30, lng: 114.18 },
  taiwan: { address: "Port of Kaohsiung, Taiwan", lat: 22.62, lng: 120.27 },
  vietnam: { address: "Cat Lai Port, Ho Chi Minh City, Vietnam", lat: 10.76, lng: 106.79 },
  india: { address: "Nhava Sheva (JNPT), Mumbai, India", lat: 18.95, lng: 72.95 },
  bangladesh: { address: "Port of Chittagong, Bangladesh", lat: 22.33, lng: 91.83 },
  pakistan: { address: "Port of Karachi, Pakistan", lat: 24.84, lng: 66.99 },
  indonesia: { address: "Tanjung Priok, Jakarta, Indonesia", lat: -6.10, lng: 106.88 },
  thailand: { address: "Laem Chabang Port, Thailand", lat: 13.08, lng: 100.88 },
  cambodia: { address: "Sihanoukville Autonomous Port, Cambodia", lat: 10.63, lng: 103.52 },
  malaysia: { address: "Port Klang, Malaysia", lat: 3.00, lng: 101.39 },
  "sri lanka": { address: "Port of Colombo, Sri Lanka", lat: 6.95, lng: 79.84 },
  "south korea": { address: "Port of Busan, South Korea", lat: 35.10, lng: 129.04 },
  korea: { address: "Port of Busan, South Korea", lat: 35.10, lng: 129.04 },
  japan: { address: "Port of Yokohama, Japan", lat: 35.45, lng: 139.65 },
  turkey: { address: "Port of Istanbul, Turkey", lat: 41.01, lng: 28.95 },
  "united kingdom": { address: "Port of Felixstowe, United Kingdom", lat: 51.96, lng: 1.35 },
  uk: { address: "Port of Felixstowe, United Kingdom", lat: 51.96, lng: 1.35 },
  germany: { address: "Port of Hamburg, Germany", lat: 53.54, lng: 9.97 },
  denmark: { address: "Port of Aarhus, Denmark", lat: 56.15, lng: 10.23 },
  greece: { address: "Port of Piraeus, Greece", lat: 37.94, lng: 23.62 },
  netherlands: { address: "Port of Rotterdam, Netherlands", lat: 51.95, lng: 4.14 },
  italy: { address: "Port of Genoa, Italy", lat: 44.41, lng: 8.93 },
  spain: { address: "Port of Valencia, Spain", lat: 39.44, lng: -0.32 },
  "united states": { address: "Port of Los Angeles, USA", lat: 33.74, lng: -118.27 },
  usa: { address: "Port of Los Angeles, USA", lat: 33.74, lng: -118.27 },
  us: { address: "Port of Los Angeles, USA", lat: 33.74, lng: -118.27 },
  mexico: { address: "Port of Manzanillo, Mexico", lat: 19.05, lng: -104.31 },
  brazil: { address: "Port of Santos, Brazil", lat: -23.96, lng: -46.30 },
  australia: { address: "Port Botany, Sydney, Australia", lat: -33.97, lng: 151.23 },
};
const getMajorPortForCountry = (country) => {
  if (!country || typeof country !== "string") return null;
  return COUNTRY_MAJOR_PORTS[country.trim().toLowerCase()] || null;
};

// Card wrapper for each step on the single-page LCA form
const lcaStepCardStyle = {
  background: '#ffffff',
  border: '1.5px solid #e5e7eb',
  borderRadius: '16px',
  padding: '32px',
  marginBottom: '24px',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)'
};

// "Next step" button rendered at the bottom of each step card.
// Validates the current step and smooth-scrolls to the next one.
function NextStepButton({ onClick }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '24px',
      paddingTop: '20px',
      borderTop: '1px solid #f1f5f9'
    }}>
      <button
        type="button"
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '10px 20px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#ffffff',
          background: 'linear-gradient(135deg, #06a457 0%, #058a47 100%)',
          border: 'none',
          borderRadius: '10px',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(6, 164, 87, 0.18)'
        }}
      >
        Next step
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
}

function LCACalculator() {
  const { productId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { supplierInfo, getToken, isStaff } = useAuth();
  // In-app links hand the product over in router state. A direct link (the
  // review link the Partner API / MCP returns, a bookmark, a pasted URL)
  // carries none, so the product is fetched by id instead — without that the
  // page waits on a product that never arrives and spins forever.
  const stateProduct = location.state?.product;
  const [fetchedProduct, setFetchedProduct] = useState(null);
  const [productLoadError, setProductLoadError] = useState("");
  const product = stateProduct || fetchedProduct;

  const [parentCategories, setParentCategories] = useState([]);
  const [parentCategoriesLoading, setParentCategoriesLoading] = useState(true);
  const [step1DataLoading, setStep1DataLoading] = useState(true);

  // "Ask manufacturer" flag basket: questions collected from the step flag
  // bars, sent via RequestModal (token-gated portal, no manufacturer account).
  const [mfrFlags, setMfrFlags] = useState([]);
  const [showMfrModal, setShowMfrModal] = useState(false);
  const canAskManufacturer =
    (MANUFACTURER_REQUESTS_LAUNCHED && ['Supplier', 'Enterprise'].includes(supplierInfo?.account_type)) || isStaff;
  const toggleMfrFlag = (flag) => {
    setMfrFlags((prev) => {
      const id = flagIdentity(flag);
      return prev.some((f) => flagIdentity(f) === id)
        ? prev.filter((f) => flagIdentity(f) !== id)
        : [...prev, flag];
    });
  };
  const mfrAuthHeaders = async () => {
    const token = await getToken?.();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };
  const [transportModes, setTransportModes] = useState([]);
  const [transportModesLoading, setTransportModesLoading] = useState(true);
  const [transportModesError, setTransportModesError] = useState("");
  const [packagingOptions, setPackagingOptions] = useState([]);
  const [decorationOptions, setDecorationOptions] = useState([]);
  const [decorationOptionsLoading, setDecorationOptionsLoading] = useState(true);
  const [decorationOptionsError, setDecorationOptionsError] = useState("");
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState("");
  const [formData, setFormData] = useState({
    // Step 1: Product Information
    productInfo: {
      name: "",
      weight: "",
      category: "",
      sku: "",
      productType: "purchased", 
      supplierDataAvailable: false,
      supplierEmissions: "",
      supplierEmissionsFileName: "",
      supplierEmissionsFileUrl: "",
    },
    // Step 2: Material Acquisition
    materialAcquisition: {
      assessmentType: "finished",
      supplierLocation: "China",
      parts: [],
      finishedProduct: {
        category: "",
        industryAverage: 0,
        parentCategorySlug: "",
        subcategorySlug: "",
        productSlug: "",
        quality: "",
        composition: null,
        processes: null,
      },
      processEmissions: 0,
    },
    // Step 3: Packaging
    packaging: {
      materials: [
        {
          type: "",
          weight: "",
        },
      ],
    },
    // Step 4: Assembly & Distribution
    assemblyDistribution: {
      manufacturing: {
        energyConsumption: "",
        assemblyLocation: "China",
        energySource: "grid",
        renewablePercentage: "",
      },
      transportation: [
        {
          mode: "sea_container",
          distance: "9007",
          originAddress: "Shanghai, China",
          originLat: 31.23,
          originLng: 121.47,
          destinationAddress: "Sydney, Australia",
          destinationLat: -33.87,
          destinationLng: 151.21,
          distanceAutoCalculated: true,
        },
      ],
    },
    decorationProcesses: [],
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // 402 body from the trial daily cap (daily_limit, next_available_at); null = modal hidden
  const [dailyLimitInfo, setDailyLimitInfo] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  // True once the assessment load attempt (or skip) has completed — used to sequence prefills.
  const [assessmentChecked, setAssessmentChecked] = useState(false);

  // AI prefill flow: 'wizard' = normal, 'ai-review' = showing suggestions panel at Step 2
  const [lcaMode, setLcaMode] = useState('wizard');
  const [aiPrefill, setAiPrefill] = useState(null); // suggestions from AI
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Auto-save status: 'idle' | 'saving' | 'saved' | 'error'
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle');
  const autoSaveTimerRef = useRef(null);

  // Render-visible mirror of editingCompletedRef. The ref alone can't drive UI,
  // which is exactly how "auto-save is off" became invisible: the indicator sat
  // at its last value and looked identical to a working auto-save.
  const [editingCompleted, setEditingCompleted] = useState(false);
  // True while the user has edits that no auto-save will pick up — either
  // because this is a completed assessment (auto-save deliberately off) or
  // because a save is still in flight / last one failed.
  const [pendingEdits, setPendingEdits] = useState(false);
  // Set when a guarded link click is intercepted; holds the href to follow if
  // the user confirms they want to leave.
  const [pendingLeaveHref, setPendingLeaveHref] = useState(null);

  // Where Step 1's SKU and weight came from, so the form can say so honestly.
  // 'catalogue' = the product record, 'detected' = parsed out of the product's
  // free-text details, null = the user typed it (no claim made).
  const [fieldSources, setFieldSources] = useState({ sku: null, weight: null });

  const hasUnsavedWork =
    (editingCompleted && pendingEdits) ||
    autoSaveStatus === 'saving' ||
    autoSaveStatus === 'error';

  // Flips on the first genuine interaction with the page. The step components
  // write to the form from their own mount effects (defaulting productType,
  // resolving a material's unit, preferring a quality, normalising the supplier
  // location), and none of that is the user's doing — gating on a real input
  // event is what separates "the form settled" from "someone changed something".
  const userInteractedRef = useRef(false);
  useEffect(() => {
    const onInteract = () => { userInteractedRef.current = true; };
    document.addEventListener('pointerdown', onInteract, true);
    document.addEventListener('keydown', onInteract, true);
    return () => {
      document.removeEventListener('pointerdown', onInteract, true);
      document.removeEventListener('keydown', onInteract, true);
    };
  }, []);

  // Called by every form mutation helper below. Deliberately NOT driven off the
  // formData effect: the prefill effects (product record, CSV transport metadata)
  // also mutate formData, and flagging those would show "you have unsaved
  // changes" for edits the user never made — which is how a warning gets ignored.
  const markEdited = () => {
    if (editingCompletedRef.current && userInteractedRef.current) setPendingEdits(true);
  };

  // Refs for each step card — used to smooth-scroll to the next step when the user clicks Next
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const step4Ref = useRef(null);
  // Skip the very first formData change after the assessment loads — that change
  // is the load itself, and we don't want to immediately echo it back to the server.
  const skipNextAutoSaveRef = useRef(true);
  // True when the assessment loaded is already complete (step >= 5). While editing
  // such an assessment we suppress auto-save so the finished results aren't reverted
  // to "in progress" on every keystroke — it's only rewritten on explicit Save.
  const editingCompletedRef = useRef(false);
  // The step the assessment was at when it loaded. An AI draft (step 4) keeps
  // that step on auto-save — see the auto-save effect.
  const loadedStepRef = useRef(0);

  // Load the product itself when it didn't come with the navigation (see above).
  useEffect(() => {
    const supplierId = supplierInfo?.supplier_id;
    if (stateProduct || !productId || !supplierId) return;
    let cancelled = false;
    setProductLoadError("");
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/products/${productId}`, {
          params: { supplier_id: supplierId },
        });
        if (cancelled) return;
        if (data?.data) setFetchedProduct(data.data);
        else setProductLoadError("We couldn't find this product in your catalogue.");
      } catch (e) {
        if (cancelled) return;
        setProductLoadError(
          e?.response?.status === 404
            ? "We couldn't find this product in your catalogue."
            : "We couldn't load this product. Check your connection and try again."
        );
      }
    })();
    return () => { cancelled = true; };
  }, [productId, stateProduct, supplierInfo?.supplier_id]);

  // Load any existing assessment for this supplier+product on mount
  useEffect(() => {
    const supplierId = supplierInfo?.supplier_id;
    if (!productId) {
      setAssessmentChecked(true); // no product context — nothing to load
      return;
    }
    // Wait for auth to provide the supplier id before deciding the assessment is
    // "fresh". supplierInfo loads asynchronously while productId is available from
    // the route immediately, so marking the assessment checked here would let the
    // prefill/auto-save effects fire against empty defaults and overwrite the saved
    // materials. The effect re-runs (supplier_id is in the deps) once auth resolves.
    if (!supplierId) return;

    // Load any existing assessment so the user can edit/recalculate it in place.
    // The form is upserted on save, so existing data is preserved until then.
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/lca/assessments`, {
          params: { supplier_id: supplierId, product_id: productId },
        });
        const assessment = data?.data;
        if (assessment) {
          // Remember whether this was a finished assessment so auto-save can be
          // suppressed while the user edits it (avoids downgrading step 5 -> 1).
          loadedStepRef.current = Number(assessment.current_step || 0);
          editingCompletedRef.current = loadedStepRef.current >= 5;
          setEditingCompleted(editingCompletedRef.current);
          // Map assessment -> UI form state
          setFormData((prev) => applyAssessmentToForm(prev, assessment));
        }
      } catch (e) {
        // Non-fatal: just start fresh
        console.warn("No existing assessment found, starting fresh");
      } finally {
        setAssessmentChecked(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, supplierInfo?.supplier_id]);

  // Load parent categories for Step 1
  useEffect(() => {
    (async () => {
      try {
        setParentCategoriesLoading(true);
        const { data } = await axios.get(`${API_BASE_URL}/taxonomy/parent-categories`);
        setParentCategories(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        // Non-fatal; fallback to static
        setParentCategories([
          { slug: 'apparel', name: 'Apparel' },
        ]);
      } finally {
        setParentCategoriesLoading(false);
      }
    })();
  }, []);

  // Load transport modes from API (no fallback; show loading/error states instead)
  useEffect(() => {
    (async () => {
      try {
        setTransportModesLoading(true);
        setTransportModesError("");
        const { data } = await axios.get(`${API_BASE_URL}/transport-modes`);
        setTransportModes(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setTransportModesError("Failed to load transport modes");
        setTransportModes([]);
      } finally {
        setTransportModesLoading(false);
      }
    })();
  }, []);

  // Load packaging materials from API
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/packaging-materials`);
        const list = Array.isArray(data?.data) ? data.data : [];
        setPackagingOptions(list);
      } catch (e) {
        setPackagingOptions([]);
      }
    })();
  }, []);

  // Load countries and codes from API

  useEffect(() => {
    (async () => {
      try {
        setCountriesLoading(true);
        setCountriesError("");
        const { data } = await axios.get(`${API_BASE_URL}/countries-and-codes`);
        setCountries(Array.isArray(data?.data) ? data.data : []);
      } catch (e) {
        setCountriesError("Failed to load countries and codes");
        setCountries([]);
      } finally {
        setCountriesLoading(false);
      }
    })();
  }, []);


  // Load decoration processes from API
  useEffect(() => {
    (async () => {
      try {
        setDecorationOptionsLoading(true);
        setDecorationOptionsError("");
        const { data } = await axios.get(`${API_BASE_URL}/decoration-processes`);
        const list = Array.isArray(data?.data) ? data.data : [];
        setDecorationOptions(list);
      } catch (e) {
        setDecorationOptionsError("Failed to load decoration processes");
        setDecorationOptions([]);
      } finally {
        setDecorationOptionsLoading(false);
      }
    })();
  }, []);

  // Material options for parts-based assessment
  const materialOptions = [
    "Cotton Fabric",
    "Polyester",
    "Nylon",
    "Wool",
    "Leather",
    "Plastic (PVC)",
    "Plastic (PP)",
    "Plastic (PE)",
    "Metal (Steel)",
    "Metal (Aluminum)",
    "Bamboo",
    "Cork",
    "Recycled Cotton",
    "Recycled Polyester",
    "Organic Cotton",
  ];

  // Derived: simple label list for Step3 dropdown (fallbacks if API fails)
  const packagingMaterials = packagingOptions.length > 0
    ? packagingOptions.map((p) => p.label)
    : [
        "Cannot load packaging materials",
      ];

  

  // Initialize form data with product information
  useEffect(() => {
    if (product && parentCategories.length > 0) {
      const apiCategory = product.supplier_category || "";
      // Match by slug (exact) or by name (case-insensitive) so that categories
      // saved as either "apparel" (slug) or "Apparel" (name) both resolve correctly.
      const matchedCategory = parentCategories.find(
        (c) => c.slug === apiCategory || c.name.toLowerCase() === apiCategory.toLowerCase()
      );
      const validatedCategory = matchedCategory ? matchedCategory.slug : "";

      // Resolve weight and remember where it came from. Only these two paths are
      // real provenance: the catalogue's own weight_grams column, or a figure
      // parsed out of the product's free-text details. Anything else is the
      // user's own typing and gets no claim attached to it.
      const prefillSku = product.code || "";
      const catalogueWeight = product.weight_grams;
      const detectedWeight = catalogueWeight == null ? extractWeightFromProduct(product) : "";

      setFieldSources({
        sku: prefillSku ? 'catalogue' : null,
        weight: catalogueWeight != null
          ? 'catalogue'
          : (Number(detectedWeight) > 0 ? 'detected' : null),
      });

      setFormData((prev) => ({
        ...prev,
        productInfo: {
          ...prev.productInfo,
          name: product.name || "",
          sku: prefillSku,
          category: validatedCategory,
          // Prefer backend weight_grams when provided; fallback to extraction
          weight: (catalogueWeight ?? detectedWeight ?? 0),
        },
        materialAcquisition: {
          ...prev.materialAcquisition,
        },
      }));

      // Mark step 1 data as loaded
      setStep1DataLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, parentCategories]);

  // Prefill transport leg from source_metadata (e.g. CSV import) — runs AFTER assessment load
  // so an existing assessment's values take precedence.
  useEffect(() => {
    if (!assessmentChecked) return;
    const meta = product?.source_metadata;
    if (!meta) return;
    const originAddress = meta.origin_supplier_location || null;
    const destinationAddress = meta.final_destination || null;
    const transportMode = meta.transport_mode || null;
    const distance = meta.distance_traveled_km;
    if (!originAddress && !destinationAddress && !transportMode && distance == null) return;

    setFormData((prev) => {
      const legs = prev.assemblyDistribution.transportation;
      const first = legs[0] || {};
      // Only overwrite the hardcoded defaults — preserve anything an assessment already set.
      const isDefaultOrigin = first.originAddress === "Shanghai, China" || !first.originAddress;
      const isDefaultDest = first.destinationAddress === "Sydney, Australia" || !first.destinationAddress;
      const isDefaultMode = first.mode === "sea_container" || !first.mode;
      const isDefaultDistance = first.distance === "9007" || first.distance === "" || first.distance == null;

      const nextFirst = { ...first };
      if (originAddress && isDefaultOrigin) {
        nextFirst.originAddress = originAddress;
        nextFirst.originLat = null;
        nextFirst.originLng = null;
      }
      if (destinationAddress && isDefaultDest) {
        nextFirst.destinationAddress = destinationAddress;
        nextFirst.destinationLat = null;
        nextFirst.destinationLng = null;
      }
      if (transportMode && isDefaultMode) {
        nextFirst.mode = normalizeTransportMode(transportMode);
      }
      if (distance != null && distance !== "" && isDefaultDistance) {
        nextFirst.distance = String(distance);
        nextFirst.distanceAutoCalculated = false;
      }

      return {
        ...prev,
        assemblyDistribution: {
          ...prev.assemblyDistribution,
          transportation: [nextFirst, ...legs.slice(1)],
        },
      };
    });
  }, [assessmentChecked, product]);

  // Prefill country-derived fields from product metadata — runs AFTER the assessment load
  // attempt completes so we don't get overridden by applyAssessmentToForm.
  useEffect(() => {
    if (!assessmentChecked) return;
    if (!product?.country_of_origin) return;
    setFormData((prev) => ({
      ...prev,
      materialAcquisition: {
        ...prev.materialAcquisition,
        // Only replace "China" (the default). If an assessment already set a specific value, keep it.
        supplierLocation:
          prev.materialAcquisition.supplierLocation !== "China"
            ? prev.materialAcquisition.supplierLocation
            : product.country_of_origin,
      },
      assemblyDistribution: {
        ...prev.assemblyDistribution,
        transportation: prev.assemblyDistribution.transportation.map((leg, i) => {
          if (i !== 0) return leg;
          // Only update the first leg's originCountry from "China" (the hardcoded
          // default). If an assessment already set a specific value, keep it.
          const nextLeg = {
            ...leg,
            originCountry:
              leg.originCountry !== "China"
                ? leg.originCountry
                : product.country_of_origin,
          };
          // The product has no specific origin address (the AI assessment may have
          // loaded an empty origin), so assume the country's major export port.
          // Only overwrite the hardcoded "Shanghai, China" default or an empty value.
          const isDefaultOrigin =
            leg.originAddress === "Shanghai, China" || !leg.originAddress;
          const port = getMajorPortForCountry(product.country_of_origin);
          if (isDefaultOrigin && port) {
            nextLeg.originAddress = port.address;
            nextLeg.originLat = port.lat;
            nextLeg.originLng = port.lng;
          }
          return nextLeg;
        }),
      },
    }));
  }, [assessmentChecked, product]);
  
  // Prefill packaging (Step 3) from the product the supplier set in the modal —
  // explicit "loose packed", canonical pcf_inputs items, or the single packaging
  // column. Runs AFTER the assessment load so a saved assessment's packaging
  // always wins; only fills when the step is still the empty default, so we never
  // clobber the user's own edits.
  useEffect(() => {
    if (!assessmentChecked) return;
    if (!product) return;
    const pcf = product.pcf_inputs || {};

    const loosePacked = !!pcf.no_packaging || LOOSE_PACKED_VALUE_RE.test(product.packaging || '');
    let rows = (Array.isArray(pcf.packaging) ? pcf.packaging : [])
      .filter((p) => p && p.type)
      .map((p) => ({ type: p.type, weight: p.weight_g > 0 ? String(p.weight_g) : '' }));
    if (loosePacked) {
      rows = [{ type: 'Loose Packed', weight: '' }];
    } else if (rows.length === 0 && product.packaging) {
      rows = [{
        type: product.packaging,
        weight: product.packaging_weight_grams != null ? String(product.packaging_weight_grams) : '',
      }];
    }
    if (rows.length === 0) return;

    setFormData((prev) => {
      // Only seed when the packaging step is untouched (all rows blank), so an
      // assessment's packaging or the user's own edits are never overwritten.
      const allEmpty = prev.packaging.materials.every((m) => !m.type && !m.weight);
      if (!allEmpty) return prev;
      return { ...prev, packaging: { ...prev.packaging, materials: rows } };
    });
  }, [assessmentChecked, product]);

  // Prefill the destination with the supplier's warehouse address — products
  // usually ship to the warehouse, so it's a good reference that makes the
  // assessment faster and better for the AI tool. Runs AFTER the assessment +
  // source_metadata prefills and only overwrites the hardcoded "Sydney, Australia"
  // default, so a real destination (from an assessment or product data) always wins.
  useEffect(() => {
    if (!assessmentChecked) return;
    const addresses = supplierInfo?.addresses;
    const warehouse = Array.isArray(addresses)
      ? addresses.find((a) => a && a.type === 'warehouse')
      : null;
    const warehouseAddress = (warehouse?.raw || '').trim();
    if (!warehouseAddress) return;

    setFormData((prev) => {
      const legs = prev.assemblyDistribution.transportation;
      const first = legs[0] || {};
      const isDefaultDest =
        first.destinationAddress === "Sydney, Australia" || !first.destinationAddress;
      if (!isDefaultDest) return prev;

      const hasCoords = warehouse.lat != null && warehouse.lng != null;
      const nextFirst = {
        ...first,
        destinationAddress: warehouseAddress,
        destinationLat: hasCoords ? warehouse.lat : null,
        destinationLng: hasCoords ? warehouse.lng : null,
      };

      // Only touch the distance if it's still the stale Shanghai→Sydney default,
      // so any distance from an import (source_metadata) is preserved.
      const isDefaultDistance =
        first.distance === "9007" || first.distance === "" || first.distance == null;
      if (isDefaultDistance) {
        if (hasCoords && first.originLat != null && first.originLng != null) {
          // Both ends have coordinates — recompute a sensible estimate.
          const rawKm = haversineKm(
            { lat: first.originLat, lon: first.originLng },
            { lat: warehouse.lat, lon: warehouse.lng }
          );
          nextFirst.distance = String(Math.round(rawKm * getModeMultiplier(first.mode)));
          nextFirst.distanceAutoCalculated = true;
        } else {
          nextFirst.distance = "";
          nextFirst.distanceAutoCalculated = false;
        }
      }

      return {
        ...prev,
        assemblyDistribution: {
          ...prev.assemblyDistribution,
          transportation: [nextFirst, ...legs.slice(1)],
        },
      };
    });
  }, [assessmentChecked, product, supplierInfo]);

  // If product data isn't available or categories haven't loaded yet, keep loading state
  useEffect(() => {
    if (!product || parentCategoriesLoading) {
      setStep1DataLoading(true);
    }
  }, [product, parentCategoriesLoading]);

  // Debounced auto-save: persist any formData change ~700ms after the user stops typing.
  // The first formData mutation after the assessment loads is the load itself, so skip it
  // (otherwise we'd round-trip the loaded data back to the server on mount).
  useEffect(() => {
    if (!assessmentChecked) return;
    if (!supplierInfo?.supplier_id || !productId) return;

    if (skipNextAutoSaveRef.current) {
      skipNextAutoSaveRef.current = false;
      return;
    }

    // Don't auto-save edits to an already-completed assessment — that would mark
    // step 1 (in progress) and lose the finished results, and would republish a
    // half-edited assessment to the public results page. It's rewritten only when
    // the user explicitly clicks Calculate Results (submitAll -> step 5).
    //
    // The banner, the beforeunload handler and the leave guard state this
    // outright (see markEdited) instead of leaving the user to infer it from an
    // auto-save indicator that never moves.
    if (editingCompletedRef.current) return;

    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('saving');
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        // < 5 = still in progress. An AI draft stays at 4 while it's reviewed:
        // the save recalculates its totals, and dropping it to 1 would make the
        // Partner API / MCP report "started, no results" for the very draft the
        // user was sent here to review.
        await saveAssessmentSnapshot(loadedStepRef.current === 4 ? 4 : 1);
        setAutoSaveStatus('saved');
      } catch (e) {
        console.error('Auto-save failed:', e);
        setAutoSaveStatus('error');
      }
    }, 700);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, assessmentChecked, supplierInfo?.supplier_id, productId]);

  // Guard a browser-level exit (reload, tab close, address bar) while there is
  // work no auto-save will pick up. Browsers only allow their own dialog here,
  // so the message text is ignored — presence is what matters.
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedWork]);

  // Guard in-app navigation. beforeunload never fires for a react-router <Link>,
  // and this app's router is a plain BrowserRouter (useBlocker needs a data
  // router), so intercept the click on the way up instead: every <Link> renders
  // an <a>, and so do the remaining hard-href links.
  useEffect(() => {
    if (!hasUnsavedWork) return;
    const onDocumentClick = (e) => {
      // Leave modified clicks, new-tab clicks and downloads alone — none of them
      // navigate this page away.
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest?.('a[href]');
      if (!anchor) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return; // external: beforeunload covers it
      if (url.pathname === window.location.pathname) return; // staying put

      e.preventDefault();
      setPendingLeaveHref(url.pathname + url.search);
    };
    document.addEventListener('click', onDocumentClick, true);
    return () => document.removeEventListener('click', onDocumentClick, true);
  }, [hasUnsavedWork]);

  // Helper function to extract weight from product data
  const extractWeightFromProduct = (product) => {
    // Look for weight info in details[].detail text, e.g. "Single weight: 9kg"
    const details = product.details;
    if (!Array.isArray(details)) return "";

    const textCandidates = [];
    for (const d of details) {
      const name = (d.name || "").toLowerCase();
      const valueText = d.detail || d.value || "";
      if (typeof valueText !== "string") continue;
      if (
        name.includes("weight") ||
        name.includes("dimension") ||
        name.includes("size") ||
        /\b(weight|kg|g)\b/i.test(valueText)
      ) {
        textCandidates.push(valueText);
      }
    }

    if (textCandidates.length === 0) return "";

    const toGrams = (num, unit) =>
      unit.toLowerCase() === "kg" ? Math.round(num * 1000) : Math.round(num);

    // Prefer an explicit "Single weight"
    for (const text of textCandidates) {
      const m = text.match(/single\s*weight\s*:\s*(\d+(?:\.\d+)?)\s*(kg|g)\b/i);
      if (m) {
        const num = parseFloat(m[1]);
        const unit = m[2];
        return toGrams(num, unit);
      }
    }

    // Otherwise, collect all weights and return the smallest (likely net weight)
    const gramsList = [];
    for (const text of textCandidates) {
      const regex = /(\d+(?:\.\d+)?)\s*(kg|g)\b/gi;
      let m;
      while ((m = regex.exec(text))) {
        gramsList.push(toGrams(parseFloat(m[1]), m[2]));
      }
    }

    if (gramsList.length > 0) {
      return Math.min(...gramsList);
    }

    return "";
  };

  // Handle form data updates
  const updateFormData = (section, field, value) => {
    markEdited();
    // The moment the user edits a prefilled field, the value is theirs — drop
    // the "from your catalogue" / "detected" claim rather than keep asserting it.
    if (section === "productInfo" && (field === "sku" || field === "weight")) {
      setFieldSources((prev) => (prev[field] ? { ...prev, [field]: null } : prev));
    }
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  // Update nested object inside a section (e.g., finishedProduct.category)
  const updateNestedFormData = (section, parentField, childField, value) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parentField]: {
          ...prev[section][parentField],
          [childField]: value,
        },
      },
    }));
  };

  // Handle array updates (for parts, packaging materials)
  const updateArrayData = (section, field, index, itemField, value) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: prev[section][field].map((item, i) =>
          i === index ? { ...item, [itemField]: value } : item
        ),
      },
    }));
  };

  // Branding/decoration processes handlers
  const addBrandingProcess = () => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      decorationProcesses: [
        ...(prev.decorationProcesses || []),
        { value: "", quantity: 1 },
      ],
    }));
  };

  const removeBrandingProcess = (index) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      decorationProcesses: (prev.decorationProcesses || []).filter((_, i) => i !== index),
    }));
  };

  const updateBrandingProcessField = (index, field, value) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      decorationProcesses: (prev.decorationProcesses || []).map((proc, i) =>
        i === index ? { ...proc, [field]: value } : proc
      ),
    }));
  };

  // Fetch AI suggestions for this product
  const fetchAiPrefill = async () => {
    setAiLoading(true);
    setAiError("");
    try {
      const token = await getToken();
      const { data } = await axios.post(
        `${API_BASE_URL}/lca/ai-prefill`,
        { product_id: productId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiPrefill(data.data);
      setLcaMode('ai-review');
    } catch (e) {
      setAiError(e.response?.data?.error || "AI suggestions failed. Try filling in manually.");
      setLcaMode('wizard');
    } finally {
      setAiLoading(false);
    }
  };

  // Apply accepted AI suggestions to form data
  const handleApplyPrefill = ({ suggestions, assessment_type, supplier_location }) => {
    markEdited();
    if (suggestions.length > 0) {
      const totalWeight = Number(formData.productInfo.weight) || 0;
      // Check if all suggestions have percentages that sum close to 100
      const percentages = suggestions.map(s => s.weight_percentage);
      const allHavePercentage = percentages.every(p => p != null && p > 0);
      const percentageSum = allHavePercentage ? percentages.reduce((a, b) => a + b, 0) : 0;
      const canCalculateWeights = allHavePercentage && percentageSum > 0 && totalWeight > 0;

      const parts = suggestions.map((s) => ({
        name: s.source_text,
        material_category: s.matched_category,
        material_subcategory: s.matched_subcategory,
        material_quality: s.matched_quality,
        // Calculate weight from percentage if available, scaled to match total if percentages don't sum to 100
        weight_grams: canCalculateWeights
          ? Math.round((s.weight_percentage / percentageSum) * totalWeight)
          : "",
        ef_kgco2_per_kg: s.ef_kgco2_per_kg,
        // Step 2 reconciles this against the material's real unit once the
        // quality options load, so an AI-matched per-item material corrects
        // itself rather than silently billing on weight.
        material_unit: s.material_unit || "per_kg",
        quantity: 1,
        process_category_id: s.process_category_id || "",
        process_id: s.process_id || "",
        process_name: s.process_name || "",
        process_ef_kgco2_per_kg: s.process_ef_kgco2_per_kg || null,
        emissions_kg: null,
        ai_suggested: true,
      }));

      setFormData((prev) => ({
        ...prev,
        materialAcquisition: {
          ...prev.materialAcquisition,
          assessmentType: 'parts',
          supplierLocation: supplier_location || prev.materialAcquisition.supplierLocation,
          parts,
        },
      }));
    }
    setLcaMode('wizard');
  };

  // Add new part to materials
  const addPart = () => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      materialAcquisition: {
        ...prev.materialAcquisition,
        parts: [
          ...prev.materialAcquisition.parts,
          {
            name: "",
            material_category: "",
            material_subcategory: "",
            material_quality: "",
            weight_grams: "",
            // 'per_kg' (default) or 'per_item' — set from the selected material's
            // unit. per_item parts bill on quantity instead of weight.
            material_unit: "per_kg",
            quantity: 1,
            ef_kgco2_per_kg: null,
            process_category_id: "",
            process_id: "",
            process_name: "",
            process_ef_kgco2_per_kg: null,
            emissions_kg: null,
          },
        ],
      },
    }));
  };

  // Remove part from materials
  const removePart = (index) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      materialAcquisition: {
        ...prev.materialAcquisition,
        parts: prev.materialAcquisition.parts.filter((_, i) => i !== index),
      },
    }));
  };

  // Add packaging material
  const addPackagingMaterial = () => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      packaging: {
        ...prev.packaging,
        materials: [
          ...prev.packaging.materials,
          {
            type: "",
            weight: "",
          },
        ],
      },
    }));
  };

  // Remove packaging material
  const removePackagingMaterial = (index) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      packaging: {
        ...prev.packaging,
        materials: prev.packaging.materials.filter((_, i) => i !== index),
      },
    }));
  };

  // Add transport leg
  const addTransportLeg = () => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      assemblyDistribution: {
        ...prev.assemblyDistribution,
        transportation: [
          ...prev.assemblyDistribution.transportation,
          { mode: "sea_container", distance: "", originAddress: "", originLat: null, originLng: null, destinationAddress: "", destinationLat: null, destinationLng: null, distanceAutoCalculated: false },
        ],
      },
    }));
  };

  // Remove transport leg
  const removeTransportLeg = (index) => {
    markEdited();
    setFormData((prev) => ({
      ...prev,
      assemblyDistribution: {
        ...prev.assemblyDistribution,
        transportation: prev.assemblyDistribution.transportation.filter((_, i) => i !== index),
      },
    }));
  };

  // Validation functions for each step
  const validateStep1 = () => {
    const errors = [];
    const { productInfo } = formData;

    if (!productInfo.name || productInfo.name.trim() === "") {
      errors.push("Product name is required");
    }
    if (productInfo.weight === "" || productInfo.weight <= 0) {
      errors.push("Product weight must be greater than 0");
    }
    if (!productInfo.sku || productInfo.sku.trim() === "") {
      errors.push("SKU/Product Code is required");
    }
    if (!productInfo.productType) {
      errors.push("Please select if the product is manufactured or purchased");
    }
    if (productInfo.productType === "purchased") {
      if (productInfo.supplierDataAvailable === null || productInfo.supplierDataAvailable === undefined) {
        errors.push("Please specify if supplier provides emissions data");
      }
      if (productInfo.supplierDataAvailable) {
        if (!productInfo.supplierEmissions || productInfo.supplierEmissions === "" || parseFloat(productInfo.supplierEmissions) <= 0) {
          errors.push("Supplier emissions value is required when supplier data is available");
        }
        if (!productInfo.supplierEmissionsFileName || productInfo.supplierEmissionsFileName.trim() === "") {
          errors.push("Documentation file is required when supplier provides emissions data");
        }
      }
    }

    return errors;
  };

  const validateStep2 = () => {
    const errors = [];
    const { materialAcquisition, decorationProcesses } = formData;

    if (!materialAcquisition.assessmentType) {
      errors.push("Please select an assessment type");
    }

    if (materialAcquisition.assessmentType === "parts") {
      if (!materialAcquisition.parts || materialAcquisition.parts.length === 0) {
        errors.push("Please add at least one material part");
      } else {
        // Validate each part
        materialAcquisition.parts.forEach((part, index) => {
          if (!part.material_category || part.material_category === "") {
            errors.push(`Part ${index + 1}: Material category is required`);
          }
          if (!part.material_subcategory || part.material_subcategory === "") {
            errors.push(`Part ${index + 1}: Material subcategory is required`);
          }
          if (!part.material_quality || part.material_quality === "") {
            errors.push(`Part ${index + 1}: Material quality is required`);
          }
          // Per-item materials are described entirely by quantity — they're
          // never weighed, so only the per-kg parts need a weight.
          if (part.material_unit === "per_item") {
            if (!part.quantity || part.quantity === "" || parseFloat(part.quantity) <= 0) {
              errors.push(`Part ${index + 1}: Quantity must be greater than 0`);
            }
          } else if (!part.weight_grams || part.weight_grams === "" || parseFloat(part.weight_grams) <= 0) {
            errors.push(`Part ${index + 1}: Weight must be greater than 0`);
          }
        });

        // Validate total weight against the product weight.
        //
        // The ±5% window is skipped when any part is per-item: those carry real
        // mass the user is never asked to measure, so the weighed parts
        // legitimately fall short and a floor would be unsatisfiable. The
        // ceiling still applies though — parts cannot outweigh the finished
        // product they came from, per-item ones included.
        const hasPerItemPart = materialAcquisition.parts.some(
          (part) => part.material_unit === "per_item"
        );
        const productWeight = Number(formData.productInfo.weight) || 0;
        const totalPartsWeight = materialAcquisition.parts.reduce((sum, part) => {
          if (part.material_unit === "per_item") return sum;
          return sum + (Number(part.weight_grams) || 0);
        }, 0);

        if (productWeight > 0) {
          if (hasPerItemPart) {
            if (totalPartsWeight > productWeight) {
              errors.push(`Weighed parts (${totalPartsWeight}g) cannot exceed the product weight (${productWeight}g)`);
            }
          } else {
            const lowerBound = productWeight * 0.95;
            const upperBound = productWeight * 1.05;

            if (totalPartsWeight < lowerBound || totalPartsWeight > upperBound) {
              errors.push(`Total parts weight (${totalPartsWeight}g) must be within ±5% of product weight (${lowerBound.toFixed(1)}g - ${upperBound.toFixed(1)}g)`);
            }
          }
        }
      }
    }

    if (materialAcquisition.assessmentType === "finished") {
      // Validate finished product workflow
      const { finishedProduct } = materialAcquisition;

      if (!finishedProduct.parentCategorySlug || finishedProduct.parentCategorySlug === "") {
        errors.push("Please select a parent category for the finished product");
      }

      if (!finishedProduct.subcategorySlug || finishedProduct.subcategorySlug === "") {
        errors.push("Please select a subcategory for the finished product");
      }
      
      if (!finishedProduct.productSlug || finishedProduct.productSlug === "") {
        errors.push("Please select a product from the subcategory");
      }
      
      if (!finishedProduct.quality || finishedProduct.quality === "") {
        errors.push("Please select a quality level for the product");
      }
    }

    // Validate branding processes if any exist
    if (decorationProcesses && decorationProcesses.length > 0) {
      decorationProcesses.forEach((proc, index) => {
        if (!proc.value || proc.value === "") {
          errors.push(`Decoration process ${index + 1}: Please select a decoration type`);
        }
      });
    }

    return errors;
  };

  const validateStep3 = () => {
    const errors = [];
    const { packaging } = formData;
    const materials = packaging.materials || [];

    // At least one packaging material is required — either a real weight or Loose Packed.
    // (A totally empty/absent list used to pass silently, letting assessments complete
    // with 0 packaging weight.)
    let hasValidEntry = false;

    materials.forEach((material, index) => {
      // Skip completely empty materials (user hasn't started filling them out)
      const isCompletelyEmpty = (!material.type || material.type === "") && (!material.weight || material.weight === "");
      // Loose Packed has a fixed carbon value — no weight required
      const isLoosePacked = material.type === 'Loose Packed';

      if (isCompletelyEmpty) return;

      // If user has started filling it out, both fields are required
      if (!material.type || material.type === "") {
        errors.push(`Packaging material ${index + 1}: Type is required`);
      }
      if (!isLoosePacked && (!material.weight || material.weight === "" || parseFloat(material.weight) <= 0)) {
        errors.push(`Packaging material ${index + 1}: Weight must be greater than 0`);
      }

      if (material.type && (isLoosePacked || parseFloat(material.weight) > 0)) {
        hasValidEntry = true;
      }
    });

    if (!hasValidEntry) {
      errors.push("Please add at least one packaging material with a weight greater than 0 (or mark it as Loose Packed)");
    }

    return errors;
  };

  const validateStep4 = () => {
    const errors = [];
    const { assemblyDistribution, productInfo } = formData;

    // Manufacturing validation (only if product is manufactured)
    if (productInfo.productType === "manufactured") {
      if (!assemblyDistribution.manufacturing.energyConsumption || 
          assemblyDistribution.manufacturing.energyConsumption === "" || 
          parseFloat(assemblyDistribution.manufacturing.energyConsumption) <= 0) {
        errors.push("Energy consumption is required and must be greater than 0");
      }
      if (!assemblyDistribution.manufacturing.assemblyLocation || 
          assemblyDistribution.manufacturing.assemblyLocation === "") {
        errors.push("Assembly location is required");
      }
    }

    // Transportation validation (always required — loop over all legs)
    const legs = assemblyDistribution.transportation;
    if (!legs || legs.length === 0) {
      errors.push("At least one transport leg is required");
    } else {
      legs.forEach((leg, i) => {
        const label = legs.length === 1 ? "" : ` (leg ${i + 1})`;
        if (!leg.mode || leg.mode === "") {
          errors.push(`Transport mode is required${label}`);
        }
        if (!leg.distance || leg.distance === "" || parseFloat(leg.distance) <= 0) {
          errors.push(`Transport distance is required and must be greater than 0${label}`);
        }
        if (!leg.originAddress || leg.originAddress === "") {
          errors.push(`Origin is required${label}`);
        }
        if (!leg.destinationAddress || leg.destinationAddress === "") {
          errors.push(`Destination is required${label}`);
        }
      });
    }

    return errors;
  };


  // Validate the given step; if it passes, smooth-scroll to the next step card.
  // If validation fails, surface that step's errors so the user can see what's missing.
  // Step 2 is skipped when the product is purchased and the supplier provides emissions data —
  // in that case, Step 1's Next jumps straight to Step 3.
  const goToNextStep = (stepNumber) => {
    const validators = { 1: validateStep1, 2: validateStep2, 3: validateStep3 };
    const errors = validators[stepNumber]();
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors([]);
    const skipStep2 =
      formData.productInfo.productType === "purchased" &&
      formData.productInfo.supplierDataAvailable === true;
    const refs = {
      1: skipStep2 ? step3Ref : step2Ref,
      2: step3Ref,
      3: step4Ref,
    };
    refs[stepNumber].current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Validate every step at once
  const validateAllSteps = () => {
    const skipMaterials =
      formData.productInfo.productType === "purchased" &&
      formData.productInfo.supplierDataAvailable === true;
    const errors = [
      ...validateStep1(),
      ...(skipMaterials ? [] : validateStep2()),
      ...validateStep3(),
      ...validateStep4(),
    ];
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Save the whole assessment in one go (final submit -> step 5)
  // Validate everything and write the finished assessment. Returns true on a
  // successful save so callers can decide where to go next — the submit button
  // goes to the summary, the leave guard goes wherever the user was headed.
  const saveAll = async () => {
    if (!validateAllSteps()) return false;
    // Cancel any pending debounced auto-save so we don't double-write
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    try {
      setLoading(true);
      setError("");
      await saveAssessmentSnapshot(5);
      // The edits are now on the server — release the unsaved-work guards before
      // navigating, or leaving this page trips its own warning.
      setPendingEdits(false);
      setAutoSaveStatus('saved');
      clearSupplierCache(supplierInfo?.supplier_id);
      return true;
    } catch (e) {
      const data = e?.response?.data;
      if (e?.response?.status === 402 && data?.code === 'DAILY_ASSESSMENT_LIMIT_REACHED') {
        setDailyLimitInfo(data);
      } else {
        setError(data?.error || "Failed to save assessment");
      }
      console.error("Failed to save single-page assessment:", e);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const submitAll = async () => {
    if (await saveAll()) navigate(`/assessment-summary/${productId}`);
  };

  // Helper: normalize legacy transport modes to new keys
  const normalizeTransportMode = (mode) => {
    if (!mode) return "sea_container";
    const m = String(mode).toLowerCase().trim();
    if (m === "sea") return "sea_container";
    if (m === "air") return "air_international";
    if (m === "rail") return "rail_freight";
    if (m === "road") return "road_hgv_diesel";
    // Friendly labels (e.g. from CSV imports)
    if (m.includes("ocean") || m.includes("ship") || m.includes("sea")) return "sea_container";
    if (m.includes("air") && m.includes("dom")) return "air_domestic";
    if (m.includes("air")) return "air_international";
    if (m.includes("rail") || m.includes("train")) return "rail_freight";
    if (m.includes("road") || m.includes("truck") || m.includes("hgv") || m.includes("van")) return "road_hgv_diesel";
    return mode; // assume already in new scheme
  };

  // Helper: normalize assemblyLocation to ensure it's always a string (country code)
  const normalizeAssemblyLocation = (location) => {
    if (!location) return "";
    // If it's an object with a code property, extract the code
    if (typeof location === 'object' && location !== null) {
      return location.code || "";
    }
    // Otherwise return as string
    return String(location);
  };

  // Map API assessment -> UI form shape
  const applyAssessmentToForm = (prev, a) => {
    const next = { ...prev };
    // Step 1
    next.productInfo = {
      ...next.productInfo,
      name: a.product_name ?? next.productInfo.name,
      sku: a.sku ?? next.productInfo.sku,
      category: a.parent_category_slug ?? a.category ?? next.productInfo.category,
      weight: a.weight_grams ?? next.productInfo.weight,
      productType: a.product_type ?? next.productInfo.productType,
      supplierDataAvailable: !!a.supplier_data_available,
      supplierEmissions: a.supplier_emissions ?? next.productInfo.supplierEmissions,
      supplierEmissionsFileName: a.supplier_emissions_file_name ?? next.productInfo.supplierEmissionsFileName,
      supplierEmissionsFileUrl: a.supplier_emissions_file_url ?? next.productInfo.supplierEmissionsFileUrl,
    };
    // Step 2
    next.materialAcquisition = {
      ...next.materialAcquisition,
      assessmentType: a.material_assessment_type ?? next.materialAcquisition.assessmentType,
      supplierLocation: a.supplier_location ?? next.materialAcquisition.supplierLocation,
      parts: Array.isArray(a.parts) ? a.parts : next.materialAcquisition.parts,
      finishedProduct: {
        category: a.finished_subcategory_slug ?? a.finished_category ?? next.materialAcquisition.finishedProduct.category,
        industryAverage: a.finished_industry_average ?? next.materialAcquisition.finishedProduct.industryAverage,
        parentCategorySlug: a.finished_parent_category_slug ?? next.productInfo.category,
        subcategorySlug: a.finished_subcategory_slug ?? null,
        productSlug: a.finished_product_slug ?? null,
        quality: a.finished_quality ?? null,
        composition: Array.isArray(a.finished_composition) ? a.finished_composition : next.materialAcquisition.finishedProduct.composition,
        processes: Array.isArray(a.finished_processes) ? a.finished_processes : next.materialAcquisition.finishedProduct.processes,
      },
      processEmissions: a.process_emissions ?? next.materialAcquisition.processEmissions,
    };
    next.decorationProcesses = Array.isArray(a.decoration_processes) ? a.decoration_processes : next.decorationProcesses;
    // Step 3
    next.packaging = {
      ...next.packaging,
      materials: Array.isArray(a.packaging_materials) ? a.packaging_materials : next.packaging.materials,
    };
    // Step 4
    next.assemblyDistribution = {
      manufacturing: {
        energyConsumption: a.manufacturing_energy_consumption_kwh ?? prev.assemblyDistribution.manufacturing.energyConsumption,
        assemblyLocation: normalizeAssemblyLocation(a.assembly_location ?? prev.assemblyDistribution.manufacturing.assemblyLocation),
        energySource: a.energy_source ?? prev.assemblyDistribution.manufacturing.energySource,
        renewablePercentage: a.renewable_percentage != null ? a.renewable_percentage : prev.assemblyDistribution.manufacturing.renewablePercentage,
      },
      transportation: Array.isArray(a.transport_legs) && a.transport_legs.length > 0
        ? a.transport_legs.map((leg) => ({
            mode: normalizeTransportMode(leg.mode),
            distance: leg.distance_km ?? "",
            // The save convention (see buildAssessmentPayload below) writes the
            // address string into `origin_country` / `destination_country`, so
            // populate both the address fields (used by Step 4's autocomplete)
            // and the country fields for backward compat.
            originAddress: leg.origin_country ?? "",
            destinationAddress: leg.destination_country ?? "",
            originLat: leg.origin_lat ?? null,
            originLng: leg.origin_lng ?? null,
            destinationLat: leg.destination_lat ?? null,
            destinationLng: leg.destination_lng ?? null,
            originCountry: leg.origin_country ?? "",
            destinationCountry: leg.destination_country ?? "",
          }))
        : [{ // fallback from legacy single-transport fields
            mode: normalizeTransportMode(a.transportation_mode ?? prev.assemblyDistribution.transportation[0]?.mode),
            distance: a.transportation_distance_km ?? prev.assemblyDistribution.transportation[0]?.distance,
            originCountry: a.origin_country ?? prev.assemblyDistribution.transportation[0]?.originCountry,
            destinationCountry: a.destination_country ?? prev.assemblyDistribution.transportation[0]?.destinationCountry,
          }],
    };
    return next;
  };

  // Build payload and upsert to server
  const saveAssessmentSnapshot = async (nextStepNumber) => {
    const supplierId = supplierInfo?.supplier_id;
    if (!supplierId) throw new Error("Missing supplier ID");
    const payload = buildAssessmentPayload(nextStepNumber);
    
    const response = await axios.post(`${API_BASE_URL}/lca/assessments`, {
      supplier_id: supplierId,
      product_id: productId,
      payload,
    });
    
    return response;
  };

  const buildAssessmentPayload = (nextStepNumber) => {
    // Build payload with RAW INPUT DATA ONLY
    // All calculations will be done server-side to protect our calculation logic
    const legs = formData.assemblyDistribution.transportation;
    const firstLeg = legs[0] || {};

    return {
      current_step: nextStepNumber,
      // Step 1: Product information
      product_name: formData.productInfo.name || null,
      sku: formData.productInfo.sku || null,
      category: formData.productInfo.category || null,
      parent_category_slug: formData.productInfo.category || null,
      weight_grams: formData.productInfo.weight === "" ? null : Number(formData.productInfo.weight),
      product_type: formData.productInfo.productType || null,
      supplier_data_available: !!formData.productInfo.supplierDataAvailable,
      supplier_emissions: formData.productInfo.supplierEmissions === "" ? null : Number(formData.productInfo.supplierEmissions),
      supplier_emissions_file_name: formData.productInfo.supplierEmissionsFileName || null,
      supplier_emissions_file_url: formData.productInfo.supplierEmissionsFileUrl || null,
      
      // Step 2: Material acquisition
      material_assessment_type: formData.materialAcquisition.assessmentType || 'parts',
      supplier_location: formData.materialAcquisition.supplierLocation || null,
      parts: formData.materialAcquisition.parts || [],
      finished_parent_category_slug: formData.materialAcquisition.finishedProduct.parentCategorySlug || formData.productInfo.category || null,
      finished_subcategory_slug: formData.materialAcquisition.finishedProduct.subcategorySlug || formData.materialAcquisition.finishedProduct.category || null,
      finished_product_slug: formData.materialAcquisition.finishedProduct.productSlug || null,
      finished_quality: formData.materialAcquisition.finishedProduct.quality || null,
      finished_composition: formData.materialAcquisition.finishedProduct.composition || null,
      finished_processes: formData.materialAcquisition.finishedProduct.processes || null,
      finished_industry_average: formData.materialAcquisition.finishedProduct.industryAverage === "" ? null : Number(formData.materialAcquisition.finishedProduct.industryAverage || 0),
      decoration_processes: formData.decorationProcesses || [],
      
      // Step 3: Packaging
      packaging_materials: formData.packaging.materials || [],
      
      // Step 4: Assembly & Distribution
      manufacturing_energy_consumption_kwh: formData.assemblyDistribution.manufacturing.energyConsumption === "" ? null : Number(formData.assemblyDistribution.manufacturing.energyConsumption),
      assembly_location: formData.assemblyDistribution.manufacturing.assemblyLocation || null,
      energy_source: formData.assemblyDistribution.manufacturing.energySource || null,
      renewable_percentage: formData.assemblyDistribution.manufacturing.energySource === "mixed" && formData.assemblyDistribution.manufacturing.renewablePercentage !== ""
        ? Number(formData.assemblyDistribution.manufacturing.renewablePercentage)
        : null,
      // New: transport legs array
      transport_legs: legs.map((leg) => ({
        mode: normalizeTransportMode(leg.mode || 'sea_container'),
        distance_km: leg.distance === "" ? null : Number(leg.distance),
        origin_country: leg.originAddress || null,
        destination_country: leg.destinationAddress || null,
        origin_lat: leg.originLat || null,
        origin_lng: leg.originLng || null,
        destination_lat: leg.destinationLat || null,
        destination_lng: leg.destinationLng || null,
      })),
      // Legacy single-transport fields (populated from first leg for backward compat)
      transportation_mode: normalizeTransportMode(firstLeg.mode || 'sea_container'),
      transportation_distance_km: firstLeg.distance === "" ? null : Number(firstLeg.distance) || null,
      origin_country: firstLeg.originAddress || null,
      destination_country: firstLeg.destinationAddress || null,
    };
  };


  return (
    <div style={{
      maxWidth: '900px',
      margin: '0 auto',
      padding: '40px 20px',
      minHeight: '100vh'
    }}>
      {/* Page Header */}
      <div style={{
        marginBottom: '40px',
        textAlign: 'center'
      }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '800',
          color: '#0f172a',
          marginBottom: '12px',
          letterSpacing: '-0.5px'
        }}>
          Product Carbon Footprint
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#64748b',
          margin: 0
        }}>
          Calculate emissions for {product?.name || 'your product'}
        </p>
        <img className="my-2" src={product?.images?.[0] || 'https://via.placeholder.com/150'} alt="Product" style={{ width: '150px', height: '150px', objectFit: 'cover', borderRadius: '8px' }} />
      </div>

      {/* AI Review Panel — shown inline at Step 2 */}
      {lcaMode === 'ai-review' && aiPrefill && (
        <AiPrefillReview
          result={aiPrefill}
          onApply={handleApplyPrefill}
          onSkip={() => setLcaMode('wizard')}
        />
      )}

      {lcaMode === 'wizard' && !product && productLoadError && (
        <div style={{ textAlign: 'center', padding: '80px 20px', color: '#64748b' }}>
          <div style={{ fontSize: '15px', marginBottom: '16px' }}>{productLoadError}</div>
          <button
            type="button"
            onClick={() => navigate('/products')}
            style={{
              padding: '10px 16px', fontSize: '14px', fontWeight: 600,
              color: '#ffffff', background: '#047c41',
              border: 'none', borderRadius: '9px', cursor: 'pointer'
            }}
          >
            Back to products
          </button>
        </div>
      )}

      {lcaMode === 'wizard' && !productLoadError && (!product || !assessmentChecked || parentCategoriesLoading || step1DataLoading) && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '80px 20px',
          color: '#64748b'
        }}>
          <div style={{
            width: '36px', height: '36px',
            border: '3px solid #e5e7eb',
            borderTopColor: '#06a457',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            marginBottom: '16px'
          }} />
          <div style={{ fontSize: '14px' }}>Loading your assessment…</div>
        </div>
      )}

      {lcaMode === 'wizard' && product && assessmentChecked && !parentCategoriesLoading && !step1DataLoading && (
        <>
          {/* Completed-assessment edit mode. Auto-save is deliberately off here
              (see the auto-save effect), so say so plainly and keep saying it —
              this bar is the difference between "nothing is saving" and an
              indicator that merely happens not to be moving. */}
          {editingCompleted && (
            <div style={{
              position: 'sticky',
              top: 0,
              zIndex: 20,
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              padding: '12px 16px',
              marginBottom: '16px',
              background: pendingEdits ? '#fffbeb' : '#f8fafc',
              border: `1px solid ${pendingEdits ? '#fcd34d' : '#e2e8f0'}`,
              borderRadius: '10px',
              fontSize: '13.5px',
              color: pendingEdits ? '#78350f' : '#475569'
            }}>
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }} aria-hidden="true">
                <circle cx="10" cy="10" r="8.25" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 6.25V10.5M10 13.25V13.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span style={{ flex: 1, minWidth: '18rem' }}>
                {pendingEdits ? (
                  <>
                    <strong>You have unsaved changes.</strong>{' '}
                    This assessment is already complete, so edits aren't saved as you
                    type — they apply when you click <strong>Calculate Results</strong>.
                  </>
                ) : (
                  <>
                    <strong>Editing a completed assessment.</strong>{' '}
                    Changes won't be saved automatically. Click{' '}
                    <strong>Calculate Results</strong> to recalculate and publish them.
                  </>
                )}
              </span>
              {pendingEdits && (
                <button
                  type="button"
                  onClick={submitAll}
                  disabled={loading}
                  style={{
                    padding: '7px 14px',
                    fontSize: '13px',
                    fontWeight: 600,
                    color: '#ffffff',
                    background: loading ? '#94a3b8' : '#047c41',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    flexShrink: 0
                  }}
                >
                  {loading ? 'Saving…' : 'Save now'}
                </button>
              )}
            </div>
          )}

          {/* Auto-save status */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            minHeight: '20px',
            marginBottom: '12px',
            fontSize: '13px'
          }}>
            {editingCompleted && !pendingEdits && (
              <span style={{ color: '#64748b' }}>Auto-save off while editing a completed assessment</span>
            )}
            {autoSaveStatus === 'saving' && (
              <span style={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <div style={{
                  width: '12px', height: '12px',
                  border: '2px solid #e5e7eb',
                  borderTopColor: '#06a457',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite'
                }} />
                Saving…
              </span>
            )}
            {autoSaveStatus === 'saved' && (
              <span style={{ color: '#06a457', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8L7 12L13 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Saved
              </span>
            )}
            {autoSaveStatus === 'error' && (
              <span style={{ color: '#dc2626' }}>Auto-save failed — your latest changes may not be persisted</span>
            )}
          </div>

          <div ref={step1Ref} style={lcaStepCardStyle}>
            <Step1ProductInfo
              formData={formData}
              fieldSources={fieldSources}
              updateFormData={updateFormData}
              validationErrors={validationErrors}
              isLoading={step1DataLoading}
            />
            {canAskManufacturer && (
              <AskManufacturerBar options={['weight', 'country_of_origin']} flags={mfrFlags} onToggle={toggleMfrFlag} />
            )}
            <NextStepButton onClick={() => goToNextStep(1)} />
          </div>

          {!(formData.productInfo.productType === "purchased" && formData.productInfo.supplierDataAvailable === true) && (
            <div ref={step2Ref} style={lcaStepCardStyle}>
              <Step2MaterialAcquisition
              formData={formData}
              materialOptions={materialOptions}
              countries={countries}
              countriesLoading={countriesLoading}
              countriesError={countriesError}
              parentCategories={parentCategories}
              decorationOptions={decorationOptions}
              decorationOptionsLoading={decorationOptionsLoading}
              decorationOptionsError={decorationOptionsError}
              updateFormData={updateFormData}
              updateNestedFormData={updateNestedFormData}
              updateArrayData={updateArrayData}
              addPart={addPart}
              removePart={removePart}
              addBrandingProcess={addBrandingProcess}
              removeBrandingProcess={removeBrandingProcess}
              updateBrandingProcessField={updateBrandingProcessField}
              validationErrors={validationErrors}
              aiPrefill={aiPrefill}
              fetchAiPrefill={fetchAiPrefill}
              aiLoading={aiLoading}
              aiError={aiError}
            />
              {canAskManufacturer && (
                <AskManufacturerBar
                  options={['parts']}
                  parts={formData.materialAcquisition.assessmentType === 'parts' ? formData.materialAcquisition.parts : null}
                  flags={mfrFlags}
                  onToggle={toggleMfrFlag}
                />
              )}
              <NextStepButton onClick={() => goToNextStep(2)} />
            </div>
          )}

          <div ref={step3Ref} style={lcaStepCardStyle}>
            <Step3Packaging
              formData={formData}
              packagingMaterials={packagingMaterials}
              updateArrayData={updateArrayData}
              addPackagingMaterial={addPackagingMaterial}
              removePackagingMaterial={removePackagingMaterial}
              validationErrors={validationErrors}
              supplierPackaging={product?.packaging || null}
            />
            {canAskManufacturer && (
              <AskManufacturerBar options={['packaging']} flags={mfrFlags} onToggle={toggleMfrFlag} />
            )}
            <NextStepButton onClick={() => goToNextStep(3)} />
          </div>

          <div ref={step4Ref} style={lcaStepCardStyle}>
            <Step4AssemblyDistribution
              formData={formData}
              countries={countries}
              countriesLoading={countriesLoading}
              countriesError={countriesError}
              transportModes={transportModes}
              transportModesLoading={transportModesLoading}
              transportModesError={transportModesError}
              updateFormData={updateFormData}
              addTransportLeg={addTransportLeg}
              removeTransportLeg={removeTransportLeg}
              validationErrors={validationErrors}
            />
            {canAskManufacturer && (
              <AskManufacturerBar options={['manufacturing', 'transport']} flags={mfrFlags} onToggle={toggleMfrFlag} />
            )}
          </div>

          {validationErrors.length > 0 && (
            <div style={{
              marginTop: '24px',
              padding: '16px 20px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#991b1b',
              fontSize: '14px'
            }}>
              <div style={{ fontWeight: 600, marginBottom: '8px' }}>Please fix the following:</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {validationErrors.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: '48px',
            paddingTop: '32px',
            borderTop: '1px solid #e5e7eb'
          }}>
            <button
              onClick={submitAll}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 28px',
                fontSize: '15px',
                fontWeight: '600',
                color: '#ffffff',
                background: loading ? '#94a3b8' : 'linear-gradient(135deg, #06a457 0%, #058a47 100%)',
                border: 'none',
                borderRadius: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: loading ? 'none' : '0 4px 12px rgba(6, 164, 87, 0.2)'
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid #ffffff',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.6s linear infinite'
                  }}></div>
                  Saving...
                </>
              ) : (
                <>
                  Calculate Results
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>
          </div>

          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '16px 20px',
              marginTop: '24px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '12px',
              color: '#991b1b',
              fontSize: '14px'
            }}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0 }}>
                <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M10 6V10M10 13V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              {error}
            </div>
          )}

          <style>{`
            @keyframes spin { to { transform: rotate(360deg); } }
          `}</style>
        </>
      )}

      {/* Ask-manufacturer basket: floating pill + prefilled request modal */}
      {canAskManufacturer && mfrFlags.length > 0 && !showMfrModal && (
        <button
          onClick={() => setShowMfrModal(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            zIndex: 1500,
            background: '#111827',
            color: '#fff',
            border: 'none',
            borderRadius: 99,
            padding: '12px 22px',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 6px 20px rgba(17,24,39,0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          🏭 Ask manufacturer
          <span style={{ background: '#fff', color: '#111827', borderRadius: 99, padding: '1px 9px', fontSize: 12, fontWeight: 700 }}>
            {mfrFlags.length}
          </span>
        </button>
      )}
      {showMfrModal && (
        <RequestModal
          supplierId={supplierInfo?.supplier_id}
          authHeaders={mfrAuthHeaders}
          prefill={{ items: [{ product_id: productId, questions: mfrFlags }] }}
          onClose={() => setShowMfrModal(false)}
          onCreated={() => setMfrFlags([])}
        />
      )}
      {/* Leave guard for in-app navigation. beforeunload can't see a <Link>
          click, so the document-level interceptor parks the destination here and
          the user decides what happens to their edits. */}
      {pendingLeaveHref && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="leaveGuardTitle"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 2000,
            background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}
        >
          <div style={{
            background: '#ffffff',
            borderRadius: '14px',
            padding: '24px',
            maxWidth: '440px',
            width: '100%',
            boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)'
          }}>
            <h2 id="leaveGuardTitle" style={{ fontSize: '18px', fontWeight: 700, color: '#0f172a', margin: '0 0 8px' }}>
              Save your changes before leaving?
            </h2>
            <p style={{ fontSize: '14px', color: '#475569', margin: '0 0 20px', lineHeight: 1.55 }}>
              {autoSaveStatus === 'error'
                ? 'Auto-save failed, so your latest edits are only in this browser. Leaving now discards them.'
                : 'This assessment is already complete, so your edits haven’t been saved yet. Leaving now discards them.'}
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setPendingLeaveHref(null)}
                style={{
                  padding: '10px 16px', fontSize: '14px', fontWeight: 600,
                  color: '#334155', background: '#ffffff',
                  border: '1px solid #cbd5e1', borderRadius: '9px', cursor: 'pointer'
                }}
              >
                Keep editing
              </button>
              <button
                type="button"
                onClick={() => {
                  const href = pendingLeaveHref;
                  setPendingEdits(false);
                  setPendingLeaveHref(null);
                  navigate(href);
                }}
                style={{
                  padding: '10px 16px', fontSize: '14px', fontWeight: 600,
                  color: '#b91c1c', background: '#ffffff',
                  border: '1px solid #fecaca', borderRadius: '9px', cursor: 'pointer'
                }}
              >
                Discard &amp; leave
              </button>
              <button
                type="button"
                disabled={loading}
                onClick={async () => {
                  const href = pendingLeaveHref;
                  setPendingLeaveHref(null);
                  // If validation fails, saveAll surfaces the errors and returns
                  // false — the user stays here to fix them, which is correct.
                  if (await saveAll()) navigate(href);
                }}
                style={{
                  padding: '10px 16px', fontSize: '14px', fontWeight: 600,
                  color: '#ffffff', background: loading ? '#94a3b8' : '#047c41',
                  border: 'none', borderRadius: '9px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Saving…' : 'Save & leave'}
              </button>
            </div>
          </div>
        </div>
      )}
      <TrialDailyLimitModal
        show={!!dailyLimitInfo}
        info={dailyLimitInfo}
        onClose={() => setDailyLimitInfo(null)}
      />
    </div>
  );
}

export default LCACalculator;
